/**
 * RecordingUploader — sends the exam recording to the server while it is being made.
 *
 * MediaRecorder hands over a slice every few seconds; each slice is uploaded straight away
 * (PUT …/recording/parts/{part}/chunks/{seq}), so a crash, power cut or closed lid
 * loses at most the slices not yet sent. Slices wait in a queue while offline and are
 * retried with backoff; they are also kept in IndexedDB, so if the browser dies while
 * offline, the next visit (same browser) uploads them before the new sitting starts.
 *
 * One uploader = one "part" (one sitting). A resumed exam gets part 2, 3… — the server
 * keeps them apart and the report's player lists them (backend: recording_parts.py).
 *
 * Uploads are sent at low fetch priority so the live interview (speech, socket, TTS)
 * goes first when the connection is busy.
 *
 * If the server can't start a part (old backend, network down at start), `failed` is
 * set and the queued slices are handed back through `onFallback`, so the recorder can
 * keep them for the old one-shot upload at the end.
 */

const DB_NAME = 'wizetok-recording';
const STORE = 'chunks';
const MAX_BACKOFF_MS = 30000;

// ---- IndexedDB (best effort: every failure just means "not persisted") -------------

function openDb() {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') { resolve(null); return; }
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const store = req.result.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex('session', 'sessionId');
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });
}

function tx(db, mode, fn) {
  return new Promise((resolve) => {
    if (!db) { resolve(null); return; }
    try {
      const t = db.transaction(STORE, mode);
      const out = fn(t.objectStore(STORE));
      t.oncomplete = () => resolve(out && 'result' in out ? out.result : null);
      t.onerror = () => resolve(null);
      t.onabort = () => resolve(null);
    } catch (e) {
      resolve(null);
    }
  });
}

const keyOf = (partId, seq) => `${partId}:${seq}`;

// ---- uploader ---------------------------------------------------------------------

export default class RecordingUploader {
  /**
   * @param {string} sessionId
   * @param {{ apiOrigin: string, getToken: () => string, onFallback?: (blobs: Blob[]) => void,
   *           fetchImpl?: typeof fetch, persist?: boolean }} options
   */
  constructor(sessionId, { apiOrigin, getToken, onFallback = () => {}, fetchImpl, persist = true }) {
    this.sessionId = sessionId;
    this.base = `${apiOrigin}/api/v1/sessions/${sessionId}/recording/parts`;
    this.getToken = getToken;
    this.onFallback = onFallback;
    this.fetch = fetchImpl || ((...a) => window.fetch(...a));
    this.persist = persist;
    this.partId = null;
    this.partNumber = null;
    this.failed = false;
    this.broken = false; // a slice the server needs is gone — stop sending to this part
    this.nextSeq = 0;
    this.queue = []; // [{ seq, blob }] in seq order
    this.uploaded = 0;
    this._pumping = false;
    this._backoff = 1000;
    this._retryTimer = null;
    this._db = null;
    this._onOnline = () => { this._backoff = 1000; void this.pump(); };
    if (typeof window !== 'undefined') window.addEventListener('online', this._onOnline);
  }

  _headers(extra = {}) {
    return { Authorization: `Bearer ${this.getToken() || ''}`, ...extra };
  }

  /** Start this sitting's part. Uploads slices left over from a crashed sitting first. */
  async begin() {
    if (this.persist) {
      this._db = await openDb();
      await this._recoverLeftovers();
    }
    try {
      const res = await this.fetch(this.base, { method: 'POST', headers: this._headers() });
      if (!res.ok) throw new Error(`start part: HTTP ${res.status}`);
      const data = await res.json();
      this.partId = data.part_id;
      this.partNumber = data.part_number;
      console.log(`🎞️ Recording part ${this.partNumber} started (continuous upload)`);
    } catch (e) {
      console.warn('⚠️ Continuous recording upload unavailable — keeping the recording in memory:', e);
      this.failed = true;
      this._detach();
      this.onFallback(this.queue.map((c) => c.blob));
      this.queue = [];
      return false;
    }
    // Slices that arrived before the part existed: persist under the real part id now.
    for (const c of this.queue) void this._save(c);
    void this.pump();
    return true;
  }

  /** Queue one MediaRecorder slice for upload. */
  push(blob) {
    if (this.failed) { this.onFallback([blob]); return; }
    const chunk = { seq: this.nextSeq++, blob };
    this.queue.push(chunk);
    if (this.partId) void this._save(chunk);
    void this.pump();
  }

  get pending() {
    return this.queue.length;
  }

  async pump() {
    if (this._pumping || !this.partId || this.broken) return;
    this._pumping = true;
    try {
      while (this.queue.length) {
        const chunk = this.queue[0];
        let res;
        try {
          res = await this.fetch(`${this.base}/${this.partId}/chunks/${chunk.seq}`, {
            method: 'PUT',
            headers: this._headers({ 'Content-Type': 'application/octet-stream' }),
            body: chunk.blob,
            priority: 'low', // the live interview's requests go first
          });
        } catch (e) {
          this._scheduleRetry(); // offline / network error — keep the slice, try again later
          return;
        }
        if (res.ok) {
          this._done(chunk);
          this._backoff = 1000;
          continue;
        }
        if (res.status === 409) {
          const body = await res.json().catch(() => ({}));
          const expected = Number(body.expected_seq);
          if (Number.isFinite(expected) && expected > chunk.seq) { this._done(chunk); continue; }
          // The server is missing an earlier slice we no longer have — this part can't grow.
          console.error('❌ Recording part is missing slice', expected, '— later slices cannot be appended');
          this.broken = true;
          return;
        }
        if (res.status >= 500 || res.status === 429) { this._scheduleRetry(); return; }
        console.error('❌ Recording slice rejected:', res.status);
        this.broken = true; // 401/404/413: retrying will not help
        return;
      }
    } finally {
      this._pumping = false;
    }
  }

  /**
   * Wait for the queue to drain (up to timeoutMs), then close the part on the server.
   * @returns {Promise<boolean>} true when every slice reached the server.
   */
  async finish(timeoutMs = 120000) {
    if (this.failed) return false;
    const until = Date.now() + timeoutMs;
    while (!this.partId && !this.failed && Date.now() < until) await new Promise((r) => setTimeout(r, 200));
    if (this.failed) return false;
    while (this.queue.length && !this.broken && Date.now() < until) {
      await this.pump();
      if (this.queue.length) await new Promise((r) => setTimeout(r, 500));
    }
    const complete = this.queue.length === 0;
    try {
      await this.fetch(`${this.base}/${this.partId}/finish`, { method: 'POST', headers: this._headers() });
    } catch (e) {
      // The server's sweeper finishes the part ~10 min after the last slice anyway.
      console.warn('⚠️ Could not close recording part (server will close it):', e);
    }
    this._detach();
    console.log(`✅ Recording part ${this.partNumber} closed — ${this.uploaded} slices${complete ? '' : `, ${this.queue.length} unsent`}`);
    return complete;
  }

  _done(chunk) {
    this.queue.shift();
    this.uploaded += 1;
    void this._forget(chunk);
  }

  _scheduleRetry() {
    if (this._retryTimer) return;
    const wait = this._backoff;
    this._backoff = Math.min(MAX_BACKOFF_MS, this._backoff * 2);
    this._retryTimer = setTimeout(() => { this._retryTimer = null; void this.pump(); }, wait);
  }

  _detach() {
    if (this._retryTimer) { clearTimeout(this._retryTimer); this._retryTimer = null; }
    if (typeof window !== 'undefined') window.removeEventListener('online', this._onOnline);
  }

  _save(chunk) {
    if (!this._db || !this.partId) return Promise.resolve();
    return tx(this._db, 'readwrite', (s) => s.put({
      key: keyOf(this.partId, chunk.seq), sessionId: this.sessionId, partId: this.partId, seq: chunk.seq, blob: chunk.blob,
    }));
  }

  _forget(chunk) {
    if (!this._db || !this.partId) return Promise.resolve();
    return tx(this._db, 'readwrite', (s) => s.delete(keyOf(this.partId, chunk.seq)));
  }

  /** Slices from an earlier sitting of this session that never reached the server. */
  async _recoverLeftovers() {
    if (!this._db) return;
    const rows = await tx(this._db, 'readonly', (s) => s.index('session').getAll(this.sessionId));
    if (!rows || !rows.length) return;
    rows.sort((a, b) => (a.partId === b.partId ? a.seq - b.seq : String(a.partId).localeCompare(String(b.partId))));
    console.log(`📤 Uploading ${rows.length} recording slices saved before the last interruption`);
    for (const row of rows) {
      try {
        const res = await this.fetch(`${this.base}/${row.partId}/chunks/${row.seq}`, {
          method: 'PUT',
          headers: this._headers({ 'Content-Type': 'application/octet-stream' }),
          body: row.blob,
        });
        if (res.status >= 500) return; // server trouble: keep them for the next visit
        // Stored, already there, or no longer accepted (part closed / gap): either way, done with it.
      } catch (e) {
        return; // still offline: keep them
      }
      await tx(this._db, 'readwrite', (s) => s.delete(row.key));
    }
  }
}
